/**
 * useEmailAccounts — Multi-Account E-Mail Hook
 * 
 * Verwaltet alle E-Mail-Konten (Gmail + IONOS/IMAP)
 * Bietet: Konten laden, E-Mails abrufen, senden, Filter, Signaturen, Vorlagen
 * 
 * Architektur:
 * - Gmail-Konten: Nutzen weiterhin /api/gmail-api
 * - IONOS/IMAP-Konten: Nutzen /api/email-api
 * - Unified Interface für das Frontend
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// ─── Types ─────────────────────────────────────────────────────

export interface EmailAccount {
  id: string;
  name: string;
  email: string;
  provider: 'gmail' | 'ionos' | 'imap_smtp' | 'outlook';
  is_active: boolean;
  is_default: boolean;
  color: string;
  last_sync_at: string | null;
  last_sync_error: string | null;
  auto_sync: boolean;
  signature_html: string | null;
  signature_text: string | null;
}

export interface EmailMessage {
  id: string;
  account_id: string;
  message_uid: string;
  message_id_header?: string;
  thread_id?: string;
  folder: string;
  subject: string;
  from_email: string;
  from_name: string;
  to_emails: string[];
  cc_emails?: string[];
  bcc_emails?: string[];
  reply_to?: string;
  date: string;
  snippet: string;
  body_text?: string;
  body_html?: string;
  is_read: boolean;
  is_starred: boolean;
  is_draft: boolean;
  has_attachments: boolean;
  attachments: any[];
}

export interface EmailFilter {
  id?: string;
  account_id: string;
  name: string;
  is_active: boolean;
  priority: number;
  condition_from?: string;
  condition_to?: string;
  condition_subject?: string;
  condition_contains?: string;
  condition_has_attachment?: boolean;
  action_move_to?: string;
  action_mark_read?: boolean;
  action_star?: boolean;
  action_forward_to?: string;
  action_label?: string;
  action_auto_reply?: string;
}

export interface EmailSignature {
  id?: string;
  account_id: string;
  name: string;
  is_default: boolean;
  signature_html: string;
  signature_text?: string;
}

export interface EmailTemplate {
  id?: string;
  account_id?: string;
  name: string;
  subject?: string;
  body_html: string;
  body_text?: string;
  category: string;
  variables?: any[];
}

export interface EmailStats {
  [folder: string]: { total: number; unread: number };
}

// ─── API Helper ─────────────────────────────────────────────

async function emailApi(action: string, params: any = {}) {
  // Get auth token from Supabase session
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }
  const resp = await fetch('/api/email-api', {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, ...params }),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Netzwerkfehler' }));
    throw new Error(err.error || `HTTP ${resp.status}`);
  }
  return resp.json();
}

// ─── Hook ───────────────────────────────────────────────────

export function useEmailAccounts() {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load accounts
  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const result = await emailApi('listAccounts');
      setAccounts(result.accounts || []);
      
      // Auto-select: "Alle Konten" wenn mehrere Konten vorhanden, sonst Default-Konto
      if (!selectedAccountId && result.accounts?.length > 0) {
        if (result.accounts.length > 1) {
          setSelectedAccountId('__all__');
        } else {
          const defaultAcc = result.accounts.find((a: EmailAccount) => a.is_default);
          setSelectedAccountId(defaultAcc?.id || result.accounts[0].id);
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    loadAccounts();
  }, []);

  const selectedAccount = accounts.find(a => a.id === selectedAccountId) || null;

  return {
    accounts,
    selectedAccount,
    selectedAccountId,
    setSelectedAccountId,
    loading,
    error,
    reload: loadAccounts,
  };
}

// ─── E-Mail Operations Hook ────────────────────────────────

export function useEmailMessages(accountId: string | null) {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [folder, setFolder] = useState('INBOX');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<EmailStats>({});

  const fetchEmails = useCallback(async (opts?: { folder?: string; search?: string; offset?: number }) => {
    if (!accountId) return;
    try {
      setLoading(true);
      const f = opts?.folder || folder;
      const s = opts?.search !== undefined ? opts.search : search;
      
      const result = await emailApi('fetchEmails', {
        accountId,
        folder: f,
        search: s || undefined,
        limit: 50,
        offset: opts?.offset || 0,
      });
      
      if (opts?.offset) {
        setEmails(prev => [...prev, ...(result.emails || [])]);
      } else {
        setEmails(result.emails || []);
      }
      setTotalCount(result.totalCount || 0);
    } catch (err: any) {
      console.error('fetchEmails error:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId, folder, search]);

  const loadStats = useCallback(async () => {
    if (!accountId) return;
    try {
      const result = await emailApi('getStats', { accountId });
      setStats(result.stats || {});
    } catch (err) {
      console.error('loadStats error:', err);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) {
      fetchEmails();
      loadStats();
    }
  }, [accountId, folder]);

  const changeFolder = useCallback((newFolder: string) => {
    setFolder(newFolder);
    setEmails([]);
  }, []);

  const getEmail = useCallback(async (emailId: string) => {
    const result = await emailApi('getEmail', { emailId });
    return result.email as EmailMessage;
  }, []);

  const sendEmail = useCallback(async (params: {
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject: string;
    bodyHtml: string;
    bodyText?: string;
    inReplyTo?: string;
    attachments?: any[];
  }) => {
    if (!accountId) throw new Error('Kein Konto ausgewählt');
    return emailApi('sendEmail', { accountId, ...params });
  }, [accountId]);

  const markRead = useCallback(async (emailId: string, isRead = true) => {
    await emailApi('markRead', { emailId, isRead });
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, is_read: isRead } : e));
  }, []);

  const markStarred = useCallback(async (emailId: string, isStarred = true) => {
    await emailApi('markStarred', { emailId, isStarred });
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, is_starred: isStarred } : e));
  }, []);

  const moveEmail = useCallback(async (emailId: string, targetFolder: string) => {
    await emailApi('moveEmail', { emailId, targetFolder });
    setEmails(prev => prev.filter(e => e.id !== emailId));
  }, []);

  const deleteEmail = useCallback(async (emailId: string, permanent = false) => {
    await emailApi('deleteEmail', { emailId, permanent });
    setEmails(prev => prev.filter(e => e.id !== emailId));
  }, []);

  const syncEmails = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const result = await emailApi('syncEmails', { accountId, folder });
      await fetchEmails();
      return result;
    } finally {
      setLoading(false);
    }
  }, [accountId, folder, fetchEmails]);

  return {
    emails,
    totalCount,
    folder,
    search,
    loading,
    stats,
    setFolder: changeFolder,
    setSearch: (s: string) => { setSearch(s); fetchEmails({ search: s }); },
    fetchEmails,
    getEmail,
    sendEmail,
    markRead,
    markStarred,
    moveEmail,
    deleteEmail,
    syncEmails,
    loadMore: () => fetchEmails({ offset: emails.length }),
  };
}

// ─── Filter Hook ────────────────────────────────────────────

export function useEmailFilters(accountId: string | null) {
  const [filters, setFilters] = useState<EmailFilter[]>([]);
  const [loading, setLoading] = useState(false);

  const loadFilters = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const result = await emailApi('getFilters', { accountId });
      setFilters(result.filters || []);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadFilters(); }, [accountId]);

  const saveFilter = useCallback(async (filter: EmailFilter) => {
    const result = await emailApi('saveFilter', { filter });
    await loadFilters();
    return result.filter;
  }, [loadFilters]);

  const deleteFilter = useCallback(async (filterId: string) => {
    await emailApi('deleteFilter', { filterId });
    setFilters(prev => prev.filter(f => f.id !== filterId));
  }, []);

  return { filters, loading, saveFilter, deleteFilter, reload: loadFilters };
}

// ─── Signaturen Hook ────────────────────────────────────────

export function useEmailSignatures(accountId: string | null) {
  const [signatures, setSignatures] = useState<EmailSignature[]>([]);
  const [loading, setLoading] = useState(false);

  const loadSignatures = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const result = await emailApi('getSignatures', { accountId });
      setSignatures(result.signatures || []);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadSignatures(); }, [accountId]);

  const saveSignature = useCallback(async (signature: EmailSignature) => {
    const result = await emailApi('saveSignature', { signature });
    await loadSignatures();
    return result.signature;
  }, [loadSignatures]);

  return { signatures, loading, saveSignature, reload: loadSignatures };
}

// ─── Vorlagen Hook ──────────────────────────────────────────

export function useEmailTemplates(accountId?: string | null) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const result = await emailApi('getTemplates', { accountId });
      setTemplates(result.templates || []);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadTemplates(); }, [accountId]);

  const saveTemplate = useCallback(async (template: EmailTemplate) => {
    const result = await emailApi('saveTemplate', { template });
    await loadTemplates();
    return result.template;
  }, [loadTemplates]);

  return { templates, loading, saveTemplate, reload: loadTemplates };
}

// ─── Konto-Verwaltung Hook ──────────────────────────────────

export function useEmailAccountSettings(accountId: string | null) {
  const updateAccount = useCallback(async (updates: Partial<EmailAccount>) => {
    if (!accountId) throw new Error('Kein Konto ausgewählt');
    return emailApi('updateAccount', { accountId, updates });
  }, [accountId]);

  const testConnection = useCallback(async () => {
    if (!accountId) throw new Error('Kein Konto ausgewählt');
    return emailApi('testConnection', { accountId });
  }, [accountId]);

  return { updateAccount, testConnection };
}
// force rebuild
